<template>
  <form ref="reportForm" class="report-form" @submit.prevent="handleSubmit">
    <!-- Loading state -->
    <div v-if="loading" class="loading">
      <span class="loading-spinner"></span>
      {{ $t("article_put_online_details.report_modal.loading") }}
    </div>

    <!-- Errors list -->
    <template v-else>
      <div class="categories-wrapper">
        <div
          v-for="(errors, category) in errorsByCategory"
          :key="category"
          class="category-section"
        >
          <div class="category-errors">
            <label
              v-for="error in errors"
              :key="error.id"
              class="error-card checkbox-wrapper"
            >
              <input
                type="checkbox"
                :value="error.id"
                :checked="selectedErrorIds.has(error.id)"
                @change="toggleError(error.id)"
              />
              <span class="custom-checkbox"></span>
              <div class="error-content">
                <span class="error-name">
                  {{ getTranslatedName(error) }}
                </span>
                <span class="error-description">
                  {{ getTranslatedDescription(error) }}
                </span>
              </div>
            </label>
          </div>
        </div>
      </div>
    </template>
  </form>
</template>

<script>
export default {
  name: "ReportProblemForm",
  
  props: {
    articleId: {
      type: String,
      required: true,
    },
    existingErrors: {
      type: Array,
      default: () => [],
    },
  },
  
  data() {
    return {
      // Structure de données simplifiée
      errorsList: [],              // Liste plate de toutes les erreurs
      selectedErrorIds: new Set(), // Set pour performance O(1)
      initialErrorIds: new Set(),  // État initial pour comparaison
      errorsByCategory: {},        // Erreurs groupées par catégorie
      // Erreurs déjà signalées que le référentiel ne propose pas (libellé
      // retiré de la liste, renommé côté back...). Elles n'ont pas de case à
      // cocher : on les conserve telles quelles pour les renvoyer au submit,
      // sinon elles seraient traitées comme "décochées" et supprimées.
      unmatchedErrors: [],
      loading: false,
    };
  },

  computed: {
    // Détection simple et claire des changements
    hasChanges() {
      // Si tailles différentes, il y a changement
      if (this.selectedErrorIds.size !== this.initialErrorIds.size) {
        return true;
      }
      
      // Vérifier si tous les éléments sont identiques
      for (let id of this.selectedErrorIds) {
        if (!this.initialErrorIds.has(id)) {
          return true;
        }
      }
      
      return false;
    },
    
    // Le bouton est désactivé uniquement si pas de changements
    isSubmitDisabled() {
      return this.loading || !this.hasChanges;
    },
    
    // Calcul des changements pour l'API
    apiChanges() {
      const toAdd = [];
      const toRemove = [];
      
      // Nouvelles erreurs à ajouter
      for (let id of this.selectedErrorIds) {
        if (!this.initialErrorIds.has(id)) {
          const error = this.errorsList.find(e => e.id === id);
          if (error && !error.dbId) {
            toAdd.push(error);
          }
        }
      }
      
      // Erreurs existantes à supprimer
      for (let id of this.initialErrorIds) {
        if (!this.selectedErrorIds.has(id)) {
          const error = this.errorsList.find(e => e.id === id);
          if (error && error.dbId) {
            toRemove.push(error);
          }
        }
      }
      
      return { toAdd, toRemove };
    },
  },

  methods: {
    // Méthode pour toggler une erreur
    toggleError(errorId) {
      if (this.selectedErrorIds.has(errorId)) {
        this.selectedErrorIds.delete(errorId);
      } else {
        this.selectedErrorIds.add(errorId);
      }
      
      // Forcer la réactivité Vue
      this.selectedErrorIds = new Set(this.selectedErrorIds);
      
      // Émettre l'état du bouton au parent
      this.$emit('update:submitDisabled', this.isSubmitDisabled);
    },
    
    // Charger les erreurs disponibles depuis l'API
    async loadErrorsList() {
      this.loading = true;
      try {
        const response = await this.$store.dispatch("getReportingList");
        this.processErrorsList(response);
      } catch (error) {
        console.error("❌ Erreur lors du chargement de la liste:", error);
        this.$emit('error', error);
      } finally {
        this.loading = false;
      }
    },
    
    // Traiter et organiser la liste des erreurs
    processErrorsList(apiResponse) {
      this.errorsList = [];
      this.errorsByCategory = {};
      
      // Parcourir les catégories et créer une structure plate
      Object.entries(apiResponse).forEach(([category, errors]) => {
        const processedErrors = errors.map((error, index) => ({
          // ID unique pour chaque erreur
          id: `${category}_${error.name}_${index}`,
          name: error.name,
          description: error.description || '',
          category: category,
          order: error.order || index,
          dbId: null, // Sera rempli si l'erreur existe déjà
        }));
        
        this.errorsByCategory[category] = processedErrors;
        this.errorsList.push(...processedErrors);
      });
    },
    
    // Normalise un libellé avant comparaison : le référentiel et la base ne
    // s'accordent pas toujours sur l'apostrophe (droite ' vs typographique ’),
    // la casse ou les espaces. Sans ça, un simple === laisse l'erreur non
    // cochée et le submit la supprime.
    normalizeReason(reason) {
      return String(reason || "")
        .replace(/[‘’ʼ]/g, "'")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase();
    },

    findErrorByReason(reason) {
      const normalized = this.normalizeReason(reason);

      return this.errorsList.find(
        error => this.normalizeReason(error.name) === normalized
      );
    },

    // Initialiser les erreurs déjà sélectionnées
    initializeSelection() {
      // Réinitialiser les sélections
      this.selectedErrorIds.clear();
      this.initialErrorIds.clear();
      this.unmatchedErrors = [];

      // Mapper les erreurs existantes avec la liste
      this.existingErrors.forEach(existing => {
        // Trouver l'erreur correspondante dans la liste
        const matchingError = this.findErrorByReason(existing.reason);

        if (matchingError) {
          // Marquer cette erreur comme existante et stocker son ID DB
          matchingError.dbId = existing.id;
          matchingError.existingDescription = existing.description;

          // Ajouter à la sélection
          this.selectedErrorIds.add(matchingError.id);
          this.initialErrorIds.add(matchingError.id);
        } else {
          // Aucune case ne représente cette erreur : on la met de côté pour la
          // renvoyer intacte au submit plutôt que de la perdre silencieusement.
          this.unmatchedErrors.push(existing);
        }
      });

      // Forcer la réactivité
      this.selectedErrorIds = new Set(this.selectedErrorIds);
      
      // Informer le parent de l'état initial
      this.$emit('update:submitDisabled', this.isSubmitDisabled);
    },
    
    // Obtenir les clés de traduction
    getCategoryKey(category) {
      const categoryMapping = {
        "📸PHOTO": "photo",
        "PHOTO": "photo",
        "INFORMATION PRODUIT": "product",
        "AUTRE": "other",
      };
      return categoryMapping[category] || category.toLowerCase();
    },
    
    getErrorKey(errorName) {
      const errorMapping = {
        "Le packshot ne correspond pas à la fiche produit": "packshot_no_match",
        "La photo marque ne correspond pas à la fiche produit": "brand_photo_no_match",
        "Packshot manquant": "packshot_missing",
        "Pas de photos packshot": "no_packshot_photos",
        "Pas de photos marques": "no_brand_photos",
        "Photo qui ne s'affiche pas correctement": "broken_photo",
        "Mauvais cadrage photo": "bad_framing",
        "Mauvaise photo": "bad_photo",
        "Mauvaise description": "bad_description",
        "Mauvais titre": "bad_title",
        "Collection manquante": "missing_collection",
        "À ne pas publier": "do_not_publish",
        "Autre": "other",
      };
      return errorMapping[errorName] || errorName.toLowerCase().replace(/\s+/g, '_');
    },
    
    // Méthodes de traduction
    getTranslatedName(error) {
      const categoryKey = this.getCategoryKey(error.category);
      const errorKey = this.getErrorKey(error.name);
      const translationKey = `article_put_online_details.report_modal.errors.${categoryKey}.${errorKey}.name`;
      
      return this.$te(translationKey) ? this.$t(translationKey) : error.name;
    },
    
    getTranslatedDescription(error) {
      const categoryKey = this.getCategoryKey(error.category);
      const errorKey = this.getErrorKey(error.name);
      const translationKey = `article_put_online_details.report_modal.errors.${categoryKey}.${errorKey}.description`;
      
      if (this.$te(translationKey)) {
        return this.$t(translationKey);
      }
      
      return error.description || error.name;
    },
    
    // Soumettre le formulaire
    handleSubmit() {
      if (this.isSubmitDisabled) {
        console.warn('⚠️ Submit attempted but no changes detected');
        return;
      }
      
      const { toAdd, toRemove } = this.apiChanges;
      
      // Préparer les données structurées pour l'API
      const submitData = {
        // Erreurs à ajouter (sans dbId)
        toAdd: toAdd.map(error => ({
          reason: error.name,
          description: error.description || ""
        })),
        
        // Erreurs à supprimer (avec leur dbId)
        toRemove: toRemove.map(error => ({
          id: error.dbId,
          reason: error.name
        })),
        
        // Liste complète des erreurs sélectionnées (pour mise à jour du statut)
        selectedErrors: []
      };
      
      // Construire la liste complète des erreurs sélectionnées
      for (let id of this.selectedErrorIds) {
        const error = this.errorsList.find(e => e.id === id);
        if (error) {
          submitData.selectedErrors.push({
            id: error.dbId,
            reason: error.name,
            description: error.description || "",
          });
        }
      }

      // Réintégrer les erreurs sans case à cocher : elles restent signalées.
      // `toRemove` ne les contient pas (elles ne sont pas dans initialErrorIds),
      // mais `selectedErrors` sert à recalculer le statut et à réécrire
      // reportReasons côté parent — les omettre les ferait disparaître de l'UI.
      this.unmatchedErrors.forEach(existing => {
        submitData.selectedErrors.push({
          id: existing.id,
          reason: existing.reason,
          description: existing.description || "",
        });
      });

      console.log('📤 Submitting changes:', {
        toAdd: submitData.toAdd.length,
        toRemove: submitData.toRemove.length,
        preserved: this.unmatchedErrors.length,
        total: submitData.selectedErrors.length
      });
      
      this.$emit("submit", submitData);
    },
    
    // Méthode pour mettre à jour l'état après validation réussie
    updateAfterSubmit(newExistingErrors) {
      console.log('🔄 Updating state after successful submit', newExistingErrors);
      
      // Réinitialiser toutes les erreurs
      this.errorsList.forEach(error => {
        error.dbId = null;
        error.existingDescription = null;
      });
      this.unmatchedErrors = [];

      // Mettre à jour avec les nouvelles erreurs existantes
      newExistingErrors.forEach(existing => {
        const matchingError = this.findErrorByReason(existing.reason);

        if (matchingError) {
          matchingError.dbId = existing.id;
          matchingError.existingDescription = existing.description;
        } else {
          // Toujours sans case correspondante : on continue de la préserver
          // pour les soumissions suivantes.
          this.unmatchedErrors.push(existing);
        }
      });
      
      // IMPORTANT: Mettre à jour l'état initial pour refléter le nouvel état
      this.initialErrorIds = new Set(this.selectedErrorIds);
      
      // Forcer la réactivité
      this.selectedErrorIds = new Set(this.selectedErrorIds);
      
      // Émettre le nouvel état du bouton
      this.$emit('update:submitDisabled', this.isSubmitDisabled);
    },
  },

  watch: {
    // Émettre l'état du bouton quand il change
    isSubmitDisabled: {
      immediate: true,
      handler(value) {
        this.$emit('update:submitDisabled', value);
      }
    },
    
    // Surveiller les changements des erreurs existantes du parent
    existingErrors: {
      deep: true,
      handler(newErrors) {
        console.log('👀 Existing errors changed from parent:', newErrors);
        // Si les erreurs ont changé après le chargement initial
        if (this.errorsList.length > 0) {
          this.updateAfterSubmit(newErrors || []);
        }
      }
    }
  },

  async created() {
    try {
      // Charger la liste des erreurs
      await this.loadErrorsList();
      
      // Initialiser la sélection après le chargement
      this.initializeSelection();
    } catch (error) {
      console.error("❌ Erreur lors de l'initialisation:", error);
    }
  },
};
</script>

<style scoped>
.report-form {
  width: 100%;
}

.categories-wrapper {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.category-section {
  margin-bottom: 8px;
}

.category-errors {
  display: flex;
  flex-direction: column;
  gap: 4px;
  border-radius: 0 0 8px 8px;
}

.error-card {
  border-radius: 8px;
  transition: all 0.2s;
}

.error-card:hover {
  background: #f9f9f9;
}

.checkbox-wrapper {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  cursor: pointer;
}

.custom-checkbox {
  width: 20px;
  height: 20px;
  border: 2px solid #000;
  border-radius: 4px;
  position: relative;
  flex-shrink: 0;
  transition: all 0.2s;
}

input[type="checkbox"] {
  display: none;
}

input[type="checkbox"]:checked + .custom-checkbox {
  background: #000;
}

input[type="checkbox"]:checked + .custom-checkbox:after {
  content: "";
  position: absolute;
  left: 5px;
  top: 1px;
  width: 5px;
  height: 10px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.error-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.error-name {
  font-weight: 500;
  font-size: 14px;
  color: #333;
}

.error-description {
  font-size: 13px;
  color: #666;
  line-height: 1.4;
}

.loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 20px;
  color: #666;
}

.loading-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #000;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>